import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { isDeepEqual } from 'react-use/lib/util';
import { Optional } from 'types/utils';

import { SelectOption } from 'components/DataEntry';
import { useSearchResult } from 'containers/Visibility/SecurityGraphNext/hooks/useSearchResult';
import { selectSecurityGraphNext } from 'containers/Visibility/SecurityGraphNext/selectors';
import { actions } from 'containers/Visibility/SecurityGraphNext/slice';
import { SourceNodeOptions } from 'containers/Visibility/SecurityGraphNext/types';
import { toSourceNodeOptions } from 'containers/Visibility/SecurityGraphNext/utils/sourceOptions';

export const useSourceNodes = () => {
  const dispatch = useDispatch();
  const {
    compactMap,
    sourceNodeOptions,
    sourcePaging,
    selectedSourceNodeOption,
    activeSearchQueryTerm,
    prevActiveSearchQueryTerm,
  } = useSelector(selectSecurityGraphNext);
  const { searchResult } = useSearchResult();
  const [sourceNodeMap, setSourceNodeMap] = useState(new Map<string, any>());

  useEffect(() => {
    // a paged list is set by the slice and grows a page at a time, so a
    // picked source's map leaves it as it is
    if (sourcePaging) return;

    const nodeMap = new Map<string, Record<string, any>>();
    searchResult.data.source_items?.forEach(group =>
      group.items?.forEach(n => nodeMap.set(n.uuid, n)),
    );
    // the response type allows a null list
    const newSourceNodeOptions: SourceNodeOptions = toSourceNodeOptions(
      searchResult.data.source_items ?? [],
    );

    const queryChanged = !isDeepEqual(
      activeSearchQueryTerm,
      prevActiveSearchQueryTerm,
    );

    if (isDeepEqual(newSourceNodeOptions, sourceNodeOptions) && !queryChanged) {
      return;
    }

    // if the search query term is not changed keep using the previous source node options
    if (
      activeSearchQueryTerm?.query === prevActiveSearchQueryTerm?.query ||
      prevActiveSearchQueryTerm?.uuid === activeSearchQueryTerm?.uuid
    ) {
      // Keep the existing pagination behavior for non-empty responses, but
      // always allow an empty successful response to clear stale options.
      if (
        newSourceNodeOptions.length > 0 &&
        newSourceNodeOptions.length <= sourceNodeOptions.length
      )
        return;
    }

    if (
      searchResult.isSuccess &&
      !isDeepEqual(newSourceNodeOptions, sourceNodeOptions)
    ) {
      setSourceNodeMap(nodeMap);
      dispatch(actions.updateSourceNodeOptions(newSourceNodeOptions ?? []));
    }
  }, [
    dispatch,
    compactMap,
    searchResult,
    sourceNodeOptions,
    sourcePaging,
    activeSearchQueryTerm,
    prevActiveSearchQueryTerm,
  ]);

  const updateSelectedSourceNodeOption = useCallback(
    (option: Optional<SelectOption>) => {
      dispatch(actions.updateSelectedSourceNodeOption(option));
    },
    [dispatch],
  );

  return {
    selectedSourceNodeOption,
    updateSelectedSourceNodeOption,
    sourceNodeOptions,
  };
};
